import { getUserPushNotificationSubscriptionForThisClient } from "~/queries/getUserPushNotificationSubscriptionForThisClient";
import { oneLine } from "common-tags";
import { withActionContext, write } from "./write";
import { AlertDialogState } from "~/dialogs/alert/AlertDialog";
import * as actions from "libs/actions";
import { generateRecordId, WebPushSubscription } from "libs/schema";
import { ClientEnvironment } from "~/environment/ClientEnvironment";
import { InvalidActionStateError } from "libs/actions";

/* -------------------------------------------------------------------------------------------------
 * subscribeToPushNotifications
 * -------------------------------------------------------------------------------------------------
 */

export const subscribeToPushNotifications = withActionContext(async (context, environment) => {
  const { currentUserId } = context;
  const ownerOrganizationId = environment.auth.getAndAssertCurrentUserOwnerOrganizationId();

  return {
    forward: async () => innerSubscribeToPushNotifications(context, environment, { ownerOrganizationId }),

    backward: async () => innerUnsubscribeFromPushNotifications(context, environment, { currentUserId }),
  };
});

/* -------------------------------------------------------------------------------------------------
 * unsubscribeFromPushNotifications
 * -------------------------------------------------------------------------------------------------
 */

export const unsubscribeFromPushNotifications = withActionContext(async (context, environment) => {
  const { currentUserId } = context;
  const ownerOrganizationId = environment.auth.getAndAssertCurrentUserOwnerOrganizationId();

  return {
    forward: async () => innerUnsubscribeFromPushNotifications(context, environment, { currentUserId }),

    backward: async () => innerSubscribeToPushNotifications(context, environment, { ownerOrganizationId }),
  };
});

/* -----------------------------------------------------------------------------------------------*/

async function innerSubscribeToPushNotifications(
  context: actions.ActionClientContext,
  environment: ClientEnvironment,
  props: {
    ownerOrganizationId: string;
  },
) {
  const { ownerOrganizationId } = props;

  // Creating the PushSubscription object requires a network request to Google's
  // servers. Here we first check to see if we already have a PushSubscription for
  // this client. If we don't, then we require the user to be online in order to
  // proceed.
  let subscription = await environment.serviceWorker.getPushNotificationSubscription();

  if (!subscription) {
    // Comms doesn't need to be online to create the "user_push_notification_subscriptions"
    // record, but creating the native PushSubscription object requires communicating
    // with Google's servers.
    if (!environment.network.isOnline()) {
      const message = oneLine`
              You appear to be offline. Unfortunately, you must be online to grant Comms
              access to push notifications.
            `;

      AlertDialogState.open({
        content: message,
      });

      throw new InvalidActionStateError(message, {
        userMessage: "SUPPRESS",
      });
    }

    // Here we prompt the user for permission if we haven't already and also create a
    // PushSubscription object
    subscription = await environment.serviceWorker.subscribeToPushNotifications();
  }

  if (!subscription) {
    throw new InvalidActionStateError("[innerSubscribeToPushNotifications] Missing browser subscription");
  }

  const action = await actions.userPushNotificationSubscription.subscribeToPushNotifications.action(
    {
      subscriptionId: generateRecordId("user_push_notification_subscriptions"),
      ownerOrganizationId,
      subscription: subscription.toJSON() as WebPushSubscription,
    },
    context,
  );

  await write(environment, { action });
}

async function innerUnsubscribeFromPushNotifications(
  context: actions.ActionClientContext,
  environment: ClientEnvironment,
  props: {
    currentUserId: string;
  },
) {
  const { currentUserId } = props;

  // While we *could* unsubscribe from the native PushSubscription object, there isn't
  // a reason to do so far as I'm aware. The native push notification object just means
  // that Comms has permission to send notifications to this client *and knows how to
  // do so.* But just deleting the "user_push_notification_subscriptions" record will
  // mean that Comms won't send any notifications to this client. Also, creating the
  // native PushSubscription takes a network request to Google's servers and seems to
  // take about 500ms, which results in a user noticeable delay between pressing the
  // "Receive push notifications" toggle in settings and the toggle showing as
  // checked (because the toggle just reflects the actual subscription state). If we
  // keep the subscription around, then toggling push notifications on and off is
  // instantaneous from the user's perspective after the first time. Also, while the
  // initial time you press the "Receive push notifications" button you need to be
  // online, subsequent toggling of the button doesn't require being online because
  // we're just reusing the same PushSubscription object that was already created by
  // the browser.
  //
  // await environment.serviceWorker.unsubscribeFromPushNotifications();

  const subscription = await getUserPushNotificationSubscriptionForThisClient(environment, {
    userId: currentUserId,
  });

  if (!subscription) return;

  const action = await actions.userPushNotificationSubscription.unsubscribeFromPushNotifications.action(
    {
      subscriptionId: subscription.id,
    },
    context,
  );

  await write(environment, { action });
}
